/**
 * MTB Page with Server-Side Search
 *
 * This version uses the in-memory search index for fast pagination, sorting, and filtering.
 * Tree data (monthly tasks/products) is disabled in this mode - organizations are flat.
 *
 * Toggle between this and MTB_PAGE via feature flag or route.
 */

import {
  GridCellParams,
  GridColDef,
  GridDensity,
  GridFilterModel,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useInlineMassUpdate } from 'api/aws/useInlineMassUpdate'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useServerSideMtb } from 'api/search/useServerSideMtb'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import MtbGridFooter from 'components/DataGrid/MtbGridFooter'
import { loadModel, saveModel } from 'components/DataGrid/operators/loadModels'
import { useDrawerContext } from 'context/DrawerProvider/DrawerProvider'
import { useMtbDrawerOpen } from 'context/DrawerProvider/useDrawerState'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { OrganizationTableItem } from 'types'
import {
  aggregationModel,
  defaultColumnOrder,
  defaultFilterModel,
  defaultSortModel,
  defaultVisibilityModel,
  MTB_EDITABLE_FIELDS,
  pinnedColumns,
} from './defaultModels'
import Drawer_MTB from './Drawer_MTB'
import { gridSxProps } from './grid/gridSxProps'
import { renderMtbColumns } from './grid/renderMtbColumns'

const CONTEXT = 'mtb'

// Editable fields that should be highlighted when multiple rows are selected

const MTBPageServerSide = () => {
  const { PK } = useParams()
  const apiRef = useGridApiRef()
  const navigate = useNavigate()
  const location = useLocation()
  const { pinned } = useLayoutContext()
  const { openMtbDrawer, closeMtbDrawer } = useDrawerContext()
  const isMtbDrawerOpen = useMtbDrawerOpen()
  const { showSnackbar } = useSnackbarContext()
  const { data: users } = useUserItems()

  const [density, setDensity] = useState<GridDensity>(() => {
    const savedDensity = localStorage.getItem(`$mtbDensityModel`) as GridDensity | null
    return savedDensity ?? 'standard'
  })
  const [selectedOrg, setSelectedOrg] = useState<OrganizationTableItem | null>(null)
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    ids: new Set<GridRowId>(),
    type: 'include',
  })

  // Load saved models from localStorage
  const savedSortModel = useMemo(() => loadModel<GridSortModel>(CONTEXT, 'SortModel', defaultSortModel), [])
  const savedFilterModel = useMemo(() => loadModel<GridFilterModel>(CONTEXT, 'FilterModel', defaultFilterModel), [])

  // Use server-side hook
  const {
    rows: serverRows,
    aggregations,
    totalRowCount,
    isLoading,
    error: searchError,
    sortModel,
    onSortModelChange: hookSortModelChange,
    filterModel,
    onFilterModelChange: hookFilterModelChange,
    serverSideProps,
    refresh,
  } = useServerSideMtb({
    enabled: true,
    pageSize: 50,
    initialSortModel: savedSortModel,
    initialFilterModel: savedFilterModel,
  })

  // Inline mass update hook - enables editing any cell to update all selected rows
  const { processRowUpdate, isUpdating } = useInlineMassUpdate<OrganizationTableItem>({
    entityType: 'organization',
    context: 'mtb',
    rowSelectionModel,
    apiRef,
    editableFields: MTB_EDITABLE_FIELDS,
    onMassUpdateComplete: (count, field) => {
      showSnackbar(`Updated "${field}" for ${count} organizations`, 'success')
      refresh() // Refresh server-side data after mass update
    },
    resolvers: {
      users: users ?? [],
    },
  })

  // Wrap change handlers to persist to localStorage
  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      saveModel(CONTEXT, 'SortModel', newSortModel)
      hookSortModelChange(newSortModel)
    },
    [hookSortModelChange]
  )

  const onFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      saveModel(CONTEXT, 'FilterModel', newFilterModel)
      hookFilterModelChange(newFilterModel)
    },
    [hookFilterModelChange]
  )

  // Process rows to add client-side computed fields
  const processedRows = useMemo(() => {
    if (!serverRows) return []

    const userById = new Map((users ?? []).map(u => [u.user_uuid, u]))
    ;(users ?? []).forEach(u => {
      if (u.id) userById.set(u.id, u)
    })

    return serverRows.map(org => {
      // Resolve team details if needed
      const team_details = org.team_details?.length
        ? org.team_details.map((u: any) => userById.get(u.user_uuid) || u).filter(Boolean)
        : []

      return {
        ...org,
        team_details,
        rowId: org.org_uuid || org.PK,
        isParent: true,
        hasChildRows: false, // Disable tree expansion in server-side mode
        id: org.org_uuid || org.PK,
      }
    })
  }, [serverRows, users])

  // Handle row click
  const handleRowClick = useCallback(
    (org: OrganizationTableItem) => {
      setSelectedOrg(org)
      openMtbDrawer()

      const { pathname } = location
      if (!/\/ORG_/.test(pathname)) {
        navigate(`/mtb/${org.PK}/main`, { replace: true })
        return
      }

      const next = pathname.replace(/\/ORG_[^/]+/, `/${org.PK}`)
      navigate(next, { replace: true })
    },
    [location, navigate, openMtbDrawer]
  )

  const changeDensity = useCallback((newDensity: GridDensity) => {
    setDensity(newDensity)
  }, [])

  const changeRowSelectionModel = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      setRowSelectionModel(newSelectionModel)
      if (newSelectionModel?.ids?.size === 1) {
        const selected = processedRows.find(o => o?.PK === Array.from(newSelectionModel?.ids)?.[0])
        setSelectedOrg(selected || null)
      }
    },
    [processedRows]
  )

  // Build columns (without tree toggle since we're flat)
  const toggleExpanded = useCallback(() => {}, [])
  const handleClickEditStart = useCallback(() => {}, [])

  const columns = useMemo(() => {
    return renderMtbColumns(density, handleRowClick, toggleExpanded, handleClickEditStart)
  }, [density, handleRowClick, toggleExpanded, handleClickEditStart]) as GridColDef[]

  // Sync selected org with URL params
  useEffect(() => {
    if (!PK || !processedRows.length) return
    const selected = processedRows.find(o => o?.PK === PK)
    if (selected) {
      setSelectedOrg(prev => (prev?.PK === selected.PK ? prev : selected))
      if (!isMtbDrawerOpen) openMtbDrawer()
    }
  }, [PK, processedRows, isMtbDrawerOpen, openMtbDrawer])

  const defaultColumnWidths = Object.fromEntries(
    columns.filter(col => typeof col.width === 'number').map(col => [col.field, col.width])
  )

  const closeDrawer = useCallback(() => {
    closeMtbDrawer()
    setSelectedOrg(null)
    navigate('/mtb', { replace: true })
  }, [closeMtbDrawer, navigate])

  const selectedRowId = useMemo(() => {
    return selectedOrg?.PK ?? null
  }, [selectedOrg])

  const isIdsRoute = /^\/mtb\/ORG_[^/]+\/[^/]+/i.test(location.pathname)

  useEffect(() => {
    if (isIdsRoute) openMtbDrawer()
    else closeMtbDrawer()
  }, [isIdsRoute, openMtbDrawer, closeMtbDrawer])

  // Highlight editable cells when multiple rows are selected (mass update mode)
  const getCellClassName = useCallback((params: GridCellParams, rowSelectionModel: GridRowSelectionModel) => {
    const isMultiSelect = rowSelectionModel?.ids?.size > 1
    const isEditableField = MTB_EDITABLE_FIELDS.includes(params.field)

    if (isMultiSelect && isEditableField) {
      return 'mass-update-target'
    }
    return ''
  }, [])

  const FooterComponentWrapper = ({ rowSelectionModel }: { rowSelectionModel: GridRowSelectionModel }) => (
    <MtbGridFooter rowSelectionModel={rowSelectionModel} serverSideMode={true} />
  )

  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={processedRows}
        apiRef={apiRef}
        context={'mtb'}
        isExpanded={() => false}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        pinnedColumns={pinnedColumns}
        aggregationModel={aggregationModel}
        onRowSelectionModelChange={changeRowSelectionModel}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={FooterComponentWrapper}
        onChangeDensity={changeDensity}
        getCellClassName={getCellClassName}
        onProcessRowUpdate={processRowUpdate}
        allowInlineMassEdit={true}
        treeData={false} // Disabled in server-side mode
        selectedRowId={selectedRowId}
        gridSxProps={gridSxProps}
        // isDrawerOpen={isMtbDrawerOpen}
        // DrawerComponent={DrawerWrapper}
        // drawerProps={{
        //   onRequestClose: closeDrawer,
        //   context: 'mtb',
        //   selectedRowId,
        //   apiRef,
        //   Component: Drawer_MTB,
        // }}
        // gridContainerSx={{
        //   position: 'relative',
        //   pt: pinned ? '60px' : 0.8,
        //   pb: 2,
        //   transition: 'all 0.3s ease-in-out',
        //   height: '100%',
        // }}
        // Server-side props
        serverSideMode
        {...serverSideProps}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        isLoading={isLoading}
      />
      {isMtbDrawerOpen && <Drawer_MTB onRequestClose={closeDrawer} />}
    </>
  )
}

export default MTBPageServerSide
